import { postJSON } from 'common/services/api'
import { normalizeErrorMessage, showToast } from 'common/utils'
import { useMutation, useQueryClient } from 'react-query'
import {
  ALLOCATE_CARER_QUERY_KEY,
  ALLOCATE_CARER_URL,
  ALLOCATION_LIST_QUERY_KEY,
  REVISION_QUERY_KEY,
  REVISIONS_QUERY_KEY
} from './queryConstants'

type AllocateCarerPayload = {
  add: { teamMemberId: string; role: string }[]
  remove: string[]
  doubleBooked?: string[]
  allowDoubleBooking?: boolean
  quickAllocation?: boolean
}

type UseAllocateCarer = {
  allocateCarer: (values: AllocateCarerPayload) => Promise<unknown>
  isAllocateLoading?: boolean
}

type UseAllocateCarerProps = {
  id?: string
  onSuccess?: () => void
}

const useAllocateCarer = ({
  id,
  onSuccess
}: UseAllocateCarerProps): UseAllocateCarer => {
  const queryClient = useQueryClient()

  const createFunction = async (values: AllocateCarerPayload) => {
    return postJSON({
      url: `${ALLOCATE_CARER_URL}/${id}`,
      method: 'POST',
      json: values,
      isRota: true
    })
  }

  const { mutateAsync: createMutation, isLoading: isAllocateLoading } =
    useMutation<unknown, unknown, AllocateCarerPayload, unknown>(
      [ALLOCATE_CARER_QUERY_KEY],
      createFunction,
      {
        onSuccess: () => {
          onSuccess?.()
          queryClient.invalidateQueries([ALLOCATION_LIST_QUERY_KEY])
          queryClient.invalidateQueries([REVISIONS_QUERY_KEY])
          queryClient.invalidateQueries([REVISION_QUERY_KEY, id])
        },
        // ROST-3942. The server refuses an allocation it cannot accept - someone deleted or
        // moved out of the home since the window was opened, or already clocked in elsewhere.
        // Without this the rejection was swallowed whole: no toast, nothing moved, and the
        // reason never reached the person who pressed Save. Same handling as
        // `useRevisionUpdate`, so the two allocation routes now report failure alike.
        onError: (err) => {
          showToast({ title: normalizeErrorMessage(err), type: 'error' })
        }
      }
    )

  /**
   * Resolves to the server's response on success and to `undefined` when the save was
   * refused, so callers can gate on the result rather than assume it went through. The
   * rejection itself is reported by `onError` above.
   */
  const allocateCarer = async (values: AllocateCarerPayload) => {
    try {
      return await createMutation(values)
    } catch {
      return undefined
    }
  }

  return {
    allocateCarer,
    isAllocateLoading
  }
}

export default useAllocateCarer
